/**
 * TreeRenderer — renders the Visual Builder's nested `blob.tree` schema
 * (Section → Row → Column → Widget) on the customer site.
 *
 * Rendering strategy:
 *   • Sections stack vertically.
 *   • Rows use CSS grid whose template columns are computed from each
 *     column's `span` (out of 12). On mobile (<768px) every column
 *     stacks to full width for a mobile-first fallback.
 *   • Columns are simple padded flex containers.
 *   • Widgets map their `comp` key to a lightweight renderer. Only
 *     safe UI widgets are rendered here (text, image, spacer, divider,
 *     button, hero, deal_banner). Product-data widgets show a "wired
 *     via legacy Homepage Sections" placeholder — legacy path stays
 *     the source of truth for product rails until a future migration.
 *
 * SAFETY: no cart / price / stock / auth logic. Every dynamic value is
 * a static presentation attribute from the blob.
 *
 * BACKWARD-COMPAT: this component renders nothing when `tree` is
 * empty, so pages without a tree behave exactly as before.
 */
import { Link } from "react-router-dom";
import { Package } from "lucide-react";

const PRODUCT_WIDGETS = new Set([
  "product_row", "product_grid", "product_card",
  "categories", "category_slider", "flash_deals", "trending",
  "best_sellers", "recommended", "buy_again", "recently_viewed", "all_products",
  "homepage_sections",
]);

export default function TreeRenderer({ tree }) {
  if (!Array.isArray(tree) || tree.length === 0) return null;
  return (
    <div className="tree-renderer" data-testid="vb-tree-renderer">
      {tree.map((sec) => sec.hidden ? null : (
        <SectionNode key={sec.id} section={sec} />
      ))}
    </div>
  );
}

function SectionNode({ section }) {
  const s = section.settings || {};
  return (
    <section
      data-testid={`vb-tree-section-${section.id}`}
      className="max-w-7xl mx-auto"
      style={{
        paddingLeft: s.padding_x, paddingRight: s.padding_x,
        paddingTop: s.padding_y, paddingBottom: s.padding_y,
        background: s.bg || undefined,
        borderRadius: s.radius || undefined,
        maxWidth: s.max_width || undefined,
      }}
    >
      {(section.children || []).map((row) => row.hidden ? null : (
        <RowNode key={row.id} row={row} />
      ))}
    </section>
  );
}

function RowNode({ row }) {
  const s = row.settings || {};
  const cols = (row.children || []).filter((c) => !c.hidden);
  if (cols.length === 0) return null;
  const template = cols.map((c) => `${Math.max(1, Math.min(12, c.span || 12))}fr`).join(" ");
  return (
    <div
      data-testid={`vb-tree-row-${row.id}`}
      className="grid grid-cols-1 md:grid-flow-col-dense"
      style={{
        gap: s.gap ?? 16,
        alignItems: s.align || "stretch",
        gridTemplateColumns: `var(--vb-tpl-mobile, 1fr)`,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @media (min-width: 768px) {
          [data-testid="vb-tree-row-${row.id}"] { grid-template-columns: ${template} !important; }
        }
      ` }} />
      {cols.map((col) => (
        <ColumnNode key={col.id} column={col} />
      ))}
    </div>
  );
}

function ColumnNode({ column }) {
  const s = column.settings || {};
  return (
    <div
      data-testid={`vb-tree-col-${column.id}`}
      style={{
        padding: s.padding,
        background: s.bg || undefined,
        alignSelf: s.align === "stretch" ? "stretch" : undefined,
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      {(column.children || []).map((w) => w.hidden ? null : (
        <WidgetNode key={w.id} widget={w} />
      ))}
    </div>
  );
}

function WidgetNode({ widget }) {
  const s = widget.settings || {};
  const testId = `vb-tree-widget-${widget.id}`;
  const wrap = (children) => (
    <div
      data-testid={testId}
      style={{
        color: s.text_color || undefined,
        background: s.bg || undefined,
        padding: s.padding ?? undefined,
        margin: s.margin ?? undefined,
        borderRadius: s.radius ?? undefined,
        textAlign: s.align || undefined,
        width: s.width || undefined,
      }}
    >{children}</div>
  );

  switch (widget.comp) {
    case "text":
      return wrap(
        <p style={{ fontSize: s.font_size, fontWeight: s.font_weight, lineHeight: 1.5, margin: 0 }}>
          {s.content || widget.label || "Text"}
        </p>,
      );
    case "image":
      return wrap(
        s.image_url
          ? <img src={s.image_url} alt={s.alt || widget.label || ""} style={{ maxWidth: "100%", height: "auto", borderRadius: s.radius }} />
          : <div className="border border-dashed border-slate-300 rounded-lg py-6 grid place-items-center text-slate-400 text-xs">Image placeholder</div>,
      );
    case "spacer":
      return <div data-testid={testId} style={{ height: s.height || 24 }} />;
    case "divider":
      return <hr data-testid={testId} style={{ borderColor: s.color || "#e2e8f0", borderTopWidth: s.thickness || 1, margin: `${s.margin ?? 8}px 0` }} />;
    case "button":
      return wrap(
        <Link
          to={s.href || "/shop"}
          className="inline-flex items-center gap-1.5 rounded-full font-semibold shadow-sm transition-opacity hover:opacity-90"
          style={{
            background: s.bg || "#E32636",
            color: s.text_color || "#fff",
            padding: s.padding || "10px 20px",
            fontSize: s.font_size || 14,
            borderRadius: s.radius ?? 999,
          }}
        >
          {s.content || widget.label || "Shop now"}
        </Link>,
      );
    case "hero":
      return (
        <div
          data-testid={testId}
          className="relative rounded-2xl overflow-hidden aspect-[16/6] bg-slate-100"
        >
          {s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}
          {(s.title || s.content) && (
            <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent flex flex-col justify-end p-6 text-white">
              {s.title && <div className="text-2xl md:text-3xl font-semibold">{s.title}</div>}
              {s.content && <div className="opacity-90 text-sm md:text-base mt-1 max-w-md">{s.content}</div>}
            </div>
          )}
        </div>
      );
    case "deal_banner":
      return (
        <Link
          to={s.href || "/shop"} data-testid={testId}
          className="block rounded-2xl overflow-hidden bg-slate-900 text-white p-5"
          style={{ background: s.bg || "linear-gradient(135deg, #E32636 0%, #c11e2b 100%)" }}
        >
          <div className="text-[10px] uppercase tracking-widest opacity-80">{s.subtitle || "Deal"}</div>
          <div className="text-lg md:text-xl font-bold">{s.content || widget.label || "Special offer"}</div>
        </Link>
      );
    default:
      if (PRODUCT_WIDGETS.has(widget.comp)) {
        return (
          <div
            data-testid={testId}
            className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 flex items-center gap-2 text-slate-500 text-[11.5px]"
          >
            <Package className="w-3.5 h-3.5" />
            <span><b>{widget.comp}</b> — product data still flows via the legacy Homepage Sections below.</span>
          </div>
        );
      }
      return wrap(
        <div className="text-slate-400 text-xs italic">Widget: {widget.comp || "unknown"}</div>,
      );
  }
}
